import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise = null;
function loadGsiScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export default function GoogleButton({ onSuccess, onError }) {
  const { loginWithGoogle } = useAuth();
  const { theme } = useTheme();
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let cancelled = false;

    loadGsiScript().then(() => {
      if (cancelled || !containerRef.current) return;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: async ({ credential }) => {
          try {
            const user = await loginWithGoogle(credential);
            onSuccess?.(user);
          } catch (err) {
            onError?.(err);
          }
        },
      });
      containerRef.current.innerHTML = "";
      window.google.accounts.id.renderButton(containerRef.current, {
        type: "standard",
        shape: "pill",
        theme: theme === "dark" ? "filled_black" : "outline",
        text: "continue_with",
        size: "large",
        width: 340,
      });
      setReady(true);
    }).catch(() => onError?.(new Error("Failed to load Google sign-in")));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  if (!CLIENT_ID) {
    return (
      <Box
        data-testid="google-signin-button"
        sx={{
          display: "flex", alignItems: "center", justifyContent: "center",
          height: 40, borderRadius: 999, border: 1, borderColor: "divider",
          color: "text.disabled", fontSize: "0.875rem", cursor: "not-allowed",
        }}
      >
        <Typography variant="body2" color="text.disabled">
          Google sign-in not configured yet
        </Typography>
      </Box>
    );
  }

  return <Box data-testid="google-signin-button" ref={containerRef} sx={{ display: "flex", justifyContent: "center", minHeight: 40, opacity: ready ? 1 : 0 }} />;
}
